import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Sparkles,
  Clock,
  Flame,
  Users,
  Pause,
  Calendar,
  Star,
  MessageSquare,
  X,
  Settings,
} from "lucide-react";
import "./SmartViews.css";

const API_URL = "/crm-wly/api/whatsapp/smartviews.php";

const iconMap = {
  sparkles: Sparkles,
  clock: Clock,
  flame: Flame,
  users: Users,
  pause: Pause,
  calendar: Calendar,
  star: Star,
  message: MessageSquare,
};

const formatarMoedaBR = (valor) => {
  const n = Number(valor || 0);
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

export default function SmartViews({ activeKey, onApplyFilter, onClearFilter }) {
  const [expanded, setExpanded] = useState(() => {
    return localStorage.getItem("smartviews_expanded") !== "0";
  });

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingCard, setLoadingCard] = useState(null);
  const [error, setError] = useState("");

  const [modalPrioridadeOpen, setModalPrioridadeOpen] = useState(false);
  const [tagsPrioridade, setTagsPrioridade] = useState([]);
  const [tagPrioridadeId, setTagPrioridadeId] = useState("");
  const [salvandoPrioridade, setSalvandoPrioridade] = useState(false);

  const [popoverPrioridadePos, setPopoverPrioridadePos] = useState(null);

  const [dragCardKey, setDragCardKey] = useState(null);

  const ordenarCardsSalvos = (cardsRecebidos) => {
    const ordemSalva = JSON.parse(
      localStorage.getItem("smartviews_order") || "[]"
    );

    if (!Array.isArray(ordemSalva) || ordemSalva.length === 0) {
      return cardsRecebidos;
    }

    return [...cardsRecebidos].sort((a, b) => {
      const ia = ordemSalva.indexOf(a.key);
      const ib = ordemSalva.indexOf(b.key);

      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;

      return ia - ib;
    });
  };

  const activeCard = useMemo(
    () => cards.find((c) => c.key === activeKey) || null,
    [cards, activeKey]
  );

  const carregarResumo = async (forceRefresh = false) => {
    try {
      setError("");
      forceRefresh ? setRefreshing(true) : setLoading(true);

      const url = `${API_URL}?mode=summary${forceRefresh ? "&refresh=1" : ""}`;

      const res = await fetch(url, {
        credentials: "include",
        cache: "no-store",
      });

      const data = await res.json();

      if (!res.ok || !data?.success) {
        throw new Error(data?.error || "Erro ao carregar SmartViews.");
      }

      const cardsRecebidos = Array.isArray(data.cards) ? data.cards : [];
      setCards(ordenarCardsSalvos(cardsRecebidos));
    } catch (err) {
      console.error("Erro SmartViews:", err);
      setError(err.message || "Erro ao carregar SmartViews.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const aplicarCard = async (card) => {
    if (!card?.key) return;

    if (activeKey === card.key) {
      onClearFilter?.();
      return;
    }

    try {
      setLoadingCard(card.key);
      setError("");

      const res = await fetch(
        `${API_URL}?mode=list&card=${encodeURIComponent(card.key)}&limit=5000`,
        {
          credentials: "include",
          cache: "no-store",
        }
      );

      const data = await res.json();

      if (!res.ok || !data?.success) {
        throw new Error(data?.error || "Erro ao aplicar filtro.");
      }

      const rows = Array.isArray(data.data) ? data.data : [];

      const leadIds = rows
        .map((r) =>
          Number(r.lead_id || r.smartview_lead_id || r.lead_id_resolvido || 0)
        )
        .filter((id) => id > 0);

      const telKeys = rows
        .map((r) => String(r.tel_key || "").trim())
        .filter(Boolean);

      const telefones = rows
        .map((r) => String(r.telefone || "").trim())
        .filter(Boolean);

      onApplyFilter?.({
        key: card.key,
        title: card.title,
        total: card.total,
        valor_total: card.valor_total,
        rows,
        leadIds,
        telKeys,
        telefones,
      });
    } catch (err) {
      console.error("Erro ao filtrar SmartView:", err);
      setError(err.message || "Erro ao aplicar filtro.");
    } finally {
      setLoadingCard(null);
    }
  };

  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      localStorage.setItem("smartviews_expanded", next ? "1" : "0");
      return next;
    });
  };

  useEffect(() => {
    carregarResumo(true);
  }, []);

  const abrirConfigPrioridade = async (e) => {
  e.stopPropagation();

  const rect = e.currentTarget.getBoundingClientRect();

  setPopoverPrioridadePos({
    top: rect.bottom + 8,
    left: rect.right - 260,
  });

  try {
    setError("");
    setModalPrioridadeOpen(true);

    const res = await fetch("/crm-wly/api/whatsapp/smartview_config.php", {
      credentials: "include",
      cache: "no-store",
    });

    const data = await res.json();

    if (!res.ok || !data?.success) {
      throw new Error(data?.error || "Erro ao carregar configuração.");
    }

    setTagsPrioridade(Array.isArray(data.tags) ? data.tags : []);
    setTagPrioridadeId(String(data.tag_prioridade_id || ""));
  } catch (err) {
    console.error(err);
    setError(err.message || "Erro ao carregar configuração.");
  }
};

  const salvarConfigPrioridade = async () => {
    if (!tagPrioridadeId) {
      alert("Escolha uma tag.");
      return;
    }

    try {
      setSalvandoPrioridade(true);

      const res = await fetch("/crm-wly/api/whatsapp/smartview_config.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          tag_prioridade_id: Number(tagPrioridadeId),
        }),
      });

      const data = await res.json();

      if (!res.ok || !data?.success) {
        throw new Error(data?.error || "Erro ao salvar configuração.");
      }

      setModalPrioridadeOpen(false);
      await carregarResumo(true);
    } catch (err) {
      console.error(err);
      alert(err.message || "Erro ao salvar configuração.");
    } finally {
      setSalvandoPrioridade(false);
    }
  };

  const moverCard = (origemKey, destinoKey) => {
    if (!origemKey || !destinoKey || origemKey === destinoKey) return;

    setCards((prev) => {
      const atual = [...prev];

      const from = atual.findIndex((c) => c.key === origemKey);
      const to = atual.findIndex((c) => c.key === destinoKey);

      if (from < 0 || to < 0) return prev;

      const [movido] = atual.splice(from, 1);
      atual.splice(to, 0, movido);

      localStorage.setItem(
        "smartviews_order",
        JSON.stringify(atual.map((c) => c.key))
      );

      return atual;
    });
  };

  return (
    <section className="smartviews-wrap">
  <div
    className={`smartviews-panel ${expanded ? "is-open" : "is-closed"}`}
  >
      <div className="smartviews-header">
        <button
          type="button"
          className="smartviews-toggle"
          onClick={toggleExpanded}
          title={expanded ? "Recolher SmartViews" : "Expandir SmartViews"}
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          <span>SmartViews</span>
        </button>

        <div className="smartviews-actions">
          {activeCard && (
            <button
              type="button"
              className="smartviews-clear"
              onClick={onClearFilter}
              title="Limpar filtro"
            >
              <X size={14} />
              {activeCard.title}
            </button>
          )}

          <button
            type="button"
            className="smartviews-refresh"
            onClick={() => carregarResumo(true)}
            disabled={refreshing || loading}
            title="Atualizar números"
          >
            <RefreshCw size={15} className={refreshing ? "spin" : ""} />
          </button>
        </div>
      </div>

      {expanded && (
        <>
          {error && <div className="smartviews-error">{error}</div>}

          <div className="smartviews-scroll">
            {loading || refreshing ? (
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="smartview-card skeleton" />
              ))
            ) : (
              cards.map((card) => {
                const isActive = activeKey === card.key;
                const isLoadingCard = loadingCard === card.key;

                return (
                  <div
                    key={card.key}
                    role="button"
                    tabIndex={0}
                    className={`smartview-card ${isActive ? "active" : ""}`}
                    draggable
                    onDragStart={(e) => {
                      setDragCardKey(card.key);
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", card.key);
                      e.currentTarget.classList.add("dragging");
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(e) => {
                      e.preventDefault();

                      const origemKey =
                        e.dataTransfer.getData("text/plain") || dragCardKey;

                      moverCard(origemKey, card.key);
                      setDragCardKey(null);
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.classList.remove("dragging");
                      setDragCardKey(null);
                    }}
                    onClick={() => {
                      if (!isLoadingCard) aplicarCard(card);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        aplicarCard(card);
                      }
                    }}
                    title={`Filtrar por ${card.title}`}
                  >
                    <div className="smartview-top">
                      <span className="smartview-title">{card.title}</span>

                      {card.key === "prioridade" && (
                        <span
                          role="button"
                          tabIndex={0}
                          className="smartview-config-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirConfigPrioridade(e);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              abrirConfigPrioridade(e);
                            }
                          }}
                          title="Configurar tag de prioridade"
                        >
                          <Settings size={14} />
                        </span>
                      )}
                    </div>

                    <div className="smartview-main">
                      {isLoadingCard ? "..." : Number(card.total || 0)}
                    </div>

                    <div className="smartview-money">
                      {formatarMoedaBR(card.valor_total)}
                    </div>

                    <div className="smartview-extra">
                      {card.key === "leads_novos" && (
                        <span>{card.extra?.ja_orcados || 0} já orçados</span>
                      )}

                      {card.key === "aguardando_resposta" && (
                        <span>
                          Maior parado:{" "}
                          {formatarMoedaBR(card.extra?.maior_orcamento || 0)}
                        </span>
                      )}

                      {card.key === "leads_quentes" && (
                        <span>
                          {card.extra?.responderam_hoje || 0} responderam hoje
                        </span>
                      )}

                      {card.key === "clientes" && (
                        <span>+{card.extra?.novos_7_dias || 0} em 7 dias</span>
                      )}

                      {card.key === "parados" && (
                        <span>
                          {formatarMoedaBR(
                            card.extra?.valor_parado_7_dias || 0
                          )}{" "}
                          há 7 dias
                        </span>
                      )}

                      {card.key === "followup_hoje" && (
                        <span>{card.extra?.atrasados || 0} atrasados</span>
                      )}

                      {card.key === "prioridade" && (
                        <span>{card.extra?.sem_resposta || 0} sem resposta</span>
                      )}

                      {card.key === "mensagens_novas" && (
                        <span>{card.extra?.conversas || 0} conversas</span>
                      )}
                    </div>

                    {isActive && (
                      <div className="smartview-active-label">Ativo</div>
                    )}
                  </div>
                );
              })
            )}

            <div
              className="smartview-card smartview-add"
              title="Criar SmartView personalizada futuramente"
            >
              <div className="smartview-plus">+</div>
              <div className="smartview-add-text">Personalizar</div>
            </div>
          </div>
        </>
      )}

{modalPrioridadeOpen && (
  <div
    className="smartview-prioridade-popover"
    style={{
      top: `${popoverPrioridadePos?.top || 120}px`,
      left: `${popoverPrioridadePos?.left || 120}px`,
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <div className="smartview-prioridade-popover-head">
      <h4>Tag Prioritária</h4>

      <button
        type="button"
        className="smartview-popover-close"
        onClick={() => setModalPrioridadeOpen(false)}
      >
        <X size={16} />
      </button>
    </div>

    <select
      value={tagPrioridadeId}
      onChange={(e) => setTagPrioridadeId(e.target.value)}
    >
      <option value="">Selecione uma tag</option>

      {tagsPrioridade.map((tag) => (
        <option key={tag.id} value={tag.id}>
          {tag.nome}
        </option>
      ))}
    </select>

    <div className="smartview-prioridade-popover-footer">
      <button onClick={salvarConfigPrioridade} disabled={salvandoPrioridade}>
        {salvandoPrioridade ? "Salvando..." : "Salvar"}
      </button>
    </div>
  </div>
)}
  </div>
</section>
  );
}